import React, { useState, useEffect } from "react";
import { base44 } from "@voult/api/base44Client";
import { Receipt, Loader2, TrendingUp, TrendingDown, DollarSign, Clock } from "lucide-react";
import { formatDistanceToNow, format } from "date-fns";
import { useAuth } from "@voult/lib/AuthContext";
import RefundControl from "@voult/components/RefundControl";

const STATUS_STYLES = {
  approved: "bg-emerald-100 text-emerald-700",
  declined: "bg-rose-100 text-rose-700",
  pending: "bg-yellow-100 text-yellow-700",
  refunded: "bg-blue-100 text-blue-700",
};

export default function Transactions() {
  const [txns, setTxns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const replace = (u) => setTxns((prev) => prev.map((t) => (t.id === u.id ? u : t)));

  useEffect(() => {
    base44.entities.PaymentTransaction.list("-created_date", 500).then(setTxns).finally(() => setLoading(false));
  }, []);

  const filtered = txns
    .filter((t) => filter === "all" || t.status === filter)
    .filter((t) => !search || [t.merchant, t.reference, t.masked_number, t.description].some((v) => v?.toLowerCase().includes(search.toLowerCase())));

  const totalApproved = txns.filter((t) => t.status === "approved").reduce((s, t) => s + (t.amount || 0), 0);
  const totalDeclined = txns.filter((t) => t.status === "declined").reduce((s, t) => s + (t.amount || 0), 0);
  const totalPending = txns.filter((t) => t.status === "pending").reduce((s, t) => s + (t.amount || 0), 0);

  const stats = [
    { label: "Total Approved", value: `$${totalApproved.toFixed(2)}`, sub: `${txns.filter((t) => t.status === "approved").length} txns`, icon: TrendingUp, color: "bg-emerald-50 text-emerald-600" },
    { label: "Declined Volume", value: `$${totalDeclined.toFixed(2)}`, sub: `${txns.filter((t) => t.status === "declined").length} txns`, icon: TrendingDown, color: "bg-rose-50 text-rose-600" },
    { label: "Pending", value: `$${totalPending.toFixed(2)}`, sub: `${txns.filter((t) => t.status === "pending").length} txns`, icon: Clock, color: "bg-yellow-50 text-yellow-600" },
    { label: "All Transactions", value: txns.length, sub: "total records", icon: DollarSign, color: "bg-indigo-50 text-indigo-600" },
  ];

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <h1 className="text-2xl font-semibold text-slate-900 mb-1">Transaction Overview</h1>
      <p className="text-sm text-slate-500 mb-6">All payment transactions linked to your vault tokens.</p>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {stats.map(({ label, value, sub, icon: Icon, color }) => (
          <div key={label} className="bg-white rounded-2xl border border-slate-200 p-4 flex items-center gap-3">
            <div className={`h-10 w-10 rounded-xl flex items-center justify-center flex-shrink-0 ${color}`}><Icon className="h-5 w-5" /></div>
            <div className="min-w-0">
              <p className="text-lg font-semibold text-slate-900 truncate">{value}</p>
              <p className="text-xs text-slate-500">{label}</p>
              <p className="text-[10px] text-slate-400">{sub}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-5">
        <input value={search} onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by merchant, reference, card…"
          className="flex-1 rounded-xl border border-slate-200 px-3.5 py-2.5 text-sm outline-none focus:border-slate-900 focus:ring-2 focus:ring-slate-900/5" />
        <div className="flex gap-1 rounded-xl bg-slate-100 p-1">
          {["all", "approved", "pending", "declined", "refunded"].map((s) => (
            <button key={s} onClick={() => setFilter(s)}
              className={`rounded-lg px-3 py-1.5 text-xs font-medium capitalize transition ${filter === s ? "bg-white shadow-sm text-slate-900" : "text-slate-500 hover:text-slate-700"}`}>
              {s}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-20 text-slate-500">
          <Receipt className="h-10 w-10 mx-auto mb-3 text-slate-300" />
          <p>{txns.length === 0 ? "No transactions recorded yet." : "No transactions match your filters."}</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          <div className="hidden sm:grid grid-cols-12 gap-4 px-5 py-2.5 border-b border-slate-100 bg-slate-50">
            <p className="col-span-4 text-xs font-semibold uppercase tracking-wider text-slate-400">Merchant</p>
            <p className="col-span-3 text-xs font-semibold uppercase tracking-wider text-slate-400">Card</p>
            <p className="col-span-2 text-xs font-semibold uppercase tracking-wider text-slate-400">Status</p>
            <p className="col-span-2 text-xs font-semibold uppercase tracking-wider text-slate-400 text-right">Amount</p>
            <p className="col-span-1 text-xs font-semibold uppercase tracking-wider text-slate-400 text-right">Date</p>
          </div>
          <div className="divide-y divide-slate-100">
            {filtered.map((txn) => (
              <div key={txn.id} data-testid="voult-txn-row" className="grid grid-cols-2 sm:grid-cols-12 gap-2 sm:gap-4 px-5 py-3.5 hover:bg-slate-50 transition">
                <div className="col-span-1 sm:col-span-4 min-w-0">
                  <p className="text-sm font-medium text-slate-900 truncate">{txn.merchant}</p>
                  {txn.description && <p className="text-xs text-slate-400 truncate">{txn.description}</p>}
                </div>
                <div className="col-span-1 sm:col-span-3">
                  <p className="font-mono text-xs text-slate-500">{txn.masked_number || "—"}</p>
                  {txn.reference && <p className="text-xs text-slate-400">{txn.reference}</p>}
                  {txn.tx_hash && <p data-testid="voult-txn-hash" className="font-mono text-[10px] text-slate-400 truncate" title={txn.tx_hash}>{txn.tx_hash.slice(0, 14)}…</p>}
                  {txn.decline_reason && <p className="text-[10px] text-rose-500">{txn.decline_reason}</p>}
                </div>
                <div className="col-span-1 sm:col-span-2">
                  <span className={`text-[10px] font-semibold uppercase rounded-full px-2 py-0.5 ${STATUS_STYLES[txn.status] || "bg-slate-100 text-slate-600"}`}>{txn.status}</span>
                </div>
                <div className="col-span-1 sm:col-span-2 text-right">
                  <p className="text-sm font-semibold text-slate-900">{txn.currency || "USD"} {(txn.amount || 0).toFixed(2)}</p>
                  {txn.refunded_amount > 0 && <p data-testid="voult-txn-refunded" className="text-[11px] text-blue-600">−{txn.refunded_amount.toFixed(2)} refunded</p>}
                  {isAdmin && txn.status === "approved" && <RefundControl txn={txn} onDone={replace} />}
                </div>
                <div className="col-span-2 sm:col-span-1 text-right">
                  <p className="text-xs text-slate-400">{txn.created_date ? formatDistanceToNow(new Date(txn.created_date), { addSuffix: true }) : "—"}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}